import "./index.css";
import { Footer, Navbar } from "@/components";
import { POINTS_TAKEOVER_ENABLED, POINTS_TAKEOVER_THEME, theme } from "@/config";
import { useCurrentRoute, useTheme } from "@/hooks";
import {
    CacheInvalidationManager,
    DialogPortal,
    NetworkChecker,
    TestControls,
    ToastPortal,
    WalletBalancePrefetcher,
    WalletSwitchDetector,
    Web3Provider,
} from "@/hoc";
import { ThemeProvider } from "@mui/material/styles";
import { HelmetProvider } from "react-helmet-async";
import { memo, useMemo } from "react";
import { DialogTriggers } from "./hoc/DialogTriggers";
import { Router } from "./Router";
import { TrackingProvider } from "./providers/TrackingProvider";
import { TrackedErrorBoundary } from "./hoc/ConditionallyTrackedErrorBoundary";
import { QuickAccessUnwinding } from "./components/quick-access/QuickAccessUnwinding";
import { OFACProvider } from "./providers/OFACProvider";
import { BackgroundFacade } from "./components/shared/BackgroundFacade";
import { TrackingConsentDialog } from "./components/tracking-consent/TrackingConsentDialog";

export const App = memo(function App() {
    const isDev = import.meta.env.DEV;
    const { themeMode, toggleTheme } = useTheme();
    const currentPath = useCurrentRoute();
    const isPointsPage = currentPath.includes("/points");

    const themeRootClassName = useMemo(
        () => "flex flex-col min-h-screen justify-start items-center min-w-[300px] bg-body mx-auto max-w-[1126px]",
        [],
    );

    // Use points takeover theme temporarily when on points page, otherwise use saved theme mode
    const currentTheme = isPointsPage && POINTS_TAKEOVER_ENABLED ? POINTS_TAKEOVER_THEME : themeMode;

    const isThemeOverrideActive = currentTheme !== themeMode;

    return (
        <HelmetProvider>
            <ThemeProvider theme={theme}>
                <TrackingProvider>
                    <TrackedErrorBoundary>
                        <Web3Provider>
                            <OFACProvider>
                                <CacheInvalidationManager>
                                    <WalletBalancePrefetcher />
                                    <NetworkChecker />
                                    <WalletSwitchDetector />
                                    <div id="app-theme-root" data-theme={currentTheme} className={themeRootClassName}>
                                        <BackgroundFacade />
                                        <Navbar
                                            themeMode={themeMode}
                                            activeTheme={currentTheme}
                                            isThemeToggleDisabled={isThemeOverrideActive}
                                            onToggleTheme={toggleTheme}
                                        />
                                        <TrackedErrorBoundary>
                                            {isDev && <TestControls />}
                                            <DialogPortal />
                                            <ToastPortal />
                                            <Router />
                                            <QuickAccessUnwinding />
                                        </TrackedErrorBoundary>
                                        <Footer />
                                        <TrackingConsentDialog />
                                        <DialogTriggers />
                                    </div>
                                </CacheInvalidationManager>
                            </OFACProvider>
                        </Web3Provider>
                    </TrackedErrorBoundary>
                </TrackingProvider>
            </ThemeProvider>
        </HelmetProvider>
    );
});

App.displayName = "App";
